///
/// EarthBrowser
///
/// Created by Matt Giger on 9/4/2024
/// Copyright (c) 2024 Geodata Labs
///

import type { ShaderAction, ShaderActions } from "@stovokor/shader/ShaderActions"
import { ShaderSourceCode, type ShaderSourceDefinition, ShaderSourceError } from "@stovokor/shader/ShaderSourceCode"
import { UniformBinding, UniformDynamic, type uniformType } from "@stovokor/shader/Uniform"
import { type AttributeLocation } from "@stovokor/shader/VertexArray"
import { AttributeBinding, VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { StovokorContext } from "@stovokor/StovokorContext"

// KHR_parallel_shader_compile: COMPLETION_STATUS_KHR, true once the driver
// has finished compiling/linking in the background
const cCompletionStatusKHR = 0x91B1
const cParallelCompileExtension = "KHR_parallel_shader_compile"
// Minimum time between completion polls while a link is in flight, so a
// frame with a hundred tile renderers asking `available` costs one query
const cPollIntervalMs = 2

let nextProgramId = 1

export class ShaderProgram {
	/** Small unique number per program, used as a draw-sort key to group draws sharing a program */
	public readonly id: number = nextProgramId++
	public errors: ShaderSourceError[] = []
	public vertex!: ShaderStage
	public fragment!: ShaderStage
	public source: ShaderSourceDefinition
	public sourceCode?: ShaderSourceCode
	public version: number = 0
	private program!: WebGLProgram
	public uniforms: Record<string, UniformBinding> = {}
	private attributes: Record<string, AttributeBinding> = {}

	private _available: boolean = false
	// A link has been issued whose result hasn't been read yet
	private linking: boolean = false
	private lastPollTime: number = -Infinity

	private context: StovokorContext

	constructor(context: StovokorContext, source: ShaderSourceDefinition) {
		this.context = context
		this.source = source
		this.createProgram(source)
	}

	/**
	 * True once the program linked successfully. While a link is in flight
	 * this polls the driver (non-blocking with KHR_parallel_shader_compile),
	 * so renderers that check it each frame pick the program up the frame
	 * it finishes instead of stalling on the compile.
	 */
	public get available(): boolean {
		if(this.linking) {
			this.poll()
		}
		return this._available
	}

	/** Whether a compile/link is still running in the background */
	public get compiling(): boolean {
		return this.linking
	}

	/**
	 * Compile and link. A program that fails stays in the cache with
	 * `available` false and `errors` populated (reported once here and via
	 * shaderDiagnostics) so renderers draw nothing instead of every user
	 * retrying the compile each frame.
	 */
	public createProgram(source: ShaderSourceDefinition): void {

		const program = this.context.gl.createProgram()
		if(!program) {
			throw new Error("Unable to create shader program")
		}
		this.program = program

		const text = this.context.getShaderSourceString(source.sourceId)
		if(text) {
			this.sourceCode = new ShaderSourceCode(text, source.defines)
			for(const include of this.sourceCode.includes) {
				const value = this.context.getShaderSourceString(include)
				this.sourceCode.applyInclude(include, value)
			}

			this.vertex = new ShaderStage(this.context, this.sourceCode.vertex, "vertex")
			this.fragment = new ShaderStage(this.context, this.sourceCode.fragment, "fragment")
			this.link()
		}
	}

	public updateShaderStage(shader: ShaderStage): void {
		if(shader.type === "vertex") {
			this.vertex = shader
		} else {
			this.fragment = shader
		}
		this.destroy()
		this.program = this.context.gl.createProgram()
		this.version += 1
		this.link()
	}

	private failed(errors: ShaderSourceError[], stage: "compile" | "link"): void {
		this._available = false
		this.errors = errors
		this.uniforms = {}
		this.attributes = {}
		const messages = errors.map(error => `${error.line}: ${error.message}`).join('\n')
		this.context.logger.error(`Shader "${this.source.sourceId}" failed to ${stage}:\n${messages}`)
	}

	/**
	 * Attach the stages and issue the link. Nothing is read back here: with
	 * KHR_parallel_shader_compile the driver compiles and links in the
	 * background and `poll` collects the result once COMPLETION_STATUS_KHR
	 * reports done. Without the extension the first poll (immediately) just
	 * blocks on LINK_STATUS as before. Stage compile errors surface through
	 * the failed link, so they no longer need a blocking COMPILE_STATUS read
	 * up front.
	 */
	protected link(): void {
		const gl = this.context.gl
		this._available = false
		gl.attachShader(this.program, this.vertex.shader)
		gl.attachShader(this.program, this.fragment.shader)
		gl.linkProgram(this.program)
		this.linking = true
		this.lastPollTime = -Infinity
		this.poll()
	}

	private poll(): void {
		const now = performance.now()
		if(now - this.lastPollTime < cPollIntervalMs) {
			return
		}
		this.lastPollTime = now

		const gl = this.context.gl
		if(this.context.hasExtension(cParallelCompileExtension)) {
			if(!gl.getProgramParameter(this.program, cCompletionStatusKHR)) {
				return
			}
		}
		this.linking = false
		this.finishLink()
		// The program may have been waited on by renderers that drew
		// nothing meanwhile; make sure the next tick paints with it
		this.context.requestRender()
	}

	private finishLink(): void {
		const gl = this.context.gl
		const program = this.program

		// Judge success by the status flag, not the info log: drivers may
		// emit warnings into the log for a program that linked fine
		if(!gl.getProgramParameter(program, WebGL2RenderingContext.LINK_STATUS)) {
			// Prefer the per-stage compile errors (they carry line numbers);
			// a link that failed with both stages compiling is a true link
			// error, reported from the program log
			this.vertex.readCompileStatus()
			this.fragment.readCompileStatus()
			const stageErrors = [...this.vertex.errors, ...this.fragment.errors]
			if(stageErrors.length > 0) {
				this.failed(stageErrors, "compile")
				return
			}
			const log = gl.getProgramInfoLog(program)?.trim() ?? ""
			let errors = ShaderSourceError.parse("", log)
			// Link errors (e.g. a uniform declared with different precision
			// in the two stages) don't carry the file:line prefix the parser
			// expects; keep the raw message rather than reporting nothing
			if(errors.length === 0) {
				errors = [new ShaderSourceError(log || "link failed", 0, 0, 0)]
			}
			this.failed(errors, "link")
		} else {
			this._available = true
			this.errors = []

			this.attributes = {}
			for (let i=gl.getProgramParameter(program, WebGL2RenderingContext.ACTIVE_ATTRIBUTES)-1;i>=0;--i) {
				const attr = gl.getActiveAttrib(program, i)
				if(attr !== null) {
					this.attributes[attr.name] = new AttributeBinding(attr, gl.getAttribLocation(program, attr.name))
				}
			}

			this.uniforms = {}
			let textureUnit = 0
			for (let i=gl.getProgramParameter(program, WebGL2RenderingContext.ACTIVE_UNIFORMS)-1;i>=0;--i) {
				const uniform = gl.getActiveUniform(program, i)
				if(uniform !== null) {
					const location = gl.getUniformLocation(program, uniform.name)
					if(location !== null) {
						const info = new UniformBinding(this.context, uniform, location)
						this.uniforms[uniform.name] = info
						if(info.isTexture) {
							info.textureUnit = textureUnit
							textureUnit += 1
						}
					}
				}
			}
		}
	}

	public destroy(): void {
		this.context.glState.useProgram(null)
		this.context.gl.deleteProgram(this.program)
	}

	public bindAction(): ShaderAction {
		return () => this.context.glState.useProgram(this.program)
	}

	public unbindAction(): ShaderAction {
		return () => this.context.glState.useProgram(null)
	}

	// iterate over uniforms and find matching properties in container
	public setupUniformActionsFrom(containers: object[]): ShaderActions {
		const actions: (() => void)[] = []
		Object.entries(this.uniforms).forEach(([name, uniform]) => {
			for(const container of containers) {
				if (name in container) {
					const uniformValue = container[name as keyof typeof container] as unknown
					if(uniformValue instanceof UniformDynamic) {
						// Do we need this? UniformDynamics are managed in the renderer now
						// uniformValue.setBinding(uniform)
						// actions.push(uniformValue.action)
					} else if(uniformValue === undefined) {
						// `in` also matches setter-only accessors (a pane's
						// `set worldDepth`) and unset fields, which read as
						// undefined; the renderer's UniformDynamic of the same
						// name supplies the value, so binding this would only
						// upload garbage and trip the type-mismatch warning
					} else {
						const action = uniform.bindingFunction(uniformValue as uniformType)
						actions.push(action)
					}
				}
			}
		})
		return actions
	}

	/**
	 * Pair each active attribute with the VertexAttribute of the same name
	 * on the container (a Geometry). Missing attributes are reported and
	 * skipped; they'd read whatever the location last pointed at.
	 */
	public attributeLocationsFrom(container: object): AttributeLocation[] {
		const bindings: AttributeLocation[] = []
		Object.entries(this.attributes).forEach(([name, attribute]) => {
			if (name in container) {
				const value = container[name as keyof typeof container] as unknown
				if(value instanceof VertexAttribute) {
					bindings.push({ attribute: value, location: attribute.location })
				} else {
					this.context.logger.warn(`Attribute ${name} is not a VertexAttribute`)
				}
			} else {
				this.context.logger.error(`Attribute ${name} not found in container`)
			}
		})
		return bindings
	}

	/**
	 * Actions binding each attribute on the current vertex array. Renderers
	 * use a VertexArray instead; this remains for one-off offscreen passes
	 * that draw on the default vertex array.
	 */
	public setupAttributeActionsFrom(container: object): ShaderActions {
		return this.attributeLocationsFrom(container).map(({ attribute, location }) =>
			() => attribute.bind(location)
		)
	}

	/**
	 * Disable the vertex attribute locations used by this shader.
	 * Run after drawing so enabled attribute arrays never outlive their draw:
	 * a location left enabled while its buffer is later deleted invalidates
	 * every subsequent draw call ("no buffer is bound to enabled attribute").
	 */
	public disableAttributeActions(): ShaderActions {
		return Object.values(this.attributes).map(attribute =>
			() => this.context.gl.disableVertexAttribArray(attribute.location)
		)
	}
}

export type ShaderStageType = "vertex" | "fragment"

export class ShaderStage {
	public source: string
	public type: ShaderStageType
	public errors: ShaderSourceError[] = []
	public shader: WebGLShader
	private context: StovokorContext

	constructor(context: StovokorContext, source: string, type: ShaderStageType) {
		this.context = context
		this.source = source
		this.type = type
		const shader = context.gl.createShader(type === "vertex" ? WebGL2RenderingContext.VERTEX_SHADER : WebGL2RenderingContext.FRAGMENT_SHADER)
		if(shader) {
			this.shader = shader
			this.compile(source)
		} else {
			throw new Error(`Unable to create shader`)
		}
	}

	public destroy(): void {
		this.context.gl.deleteShader(this.shader)
	}

	/**
	 * Issue the compile. The result isn't read here: querying it would
	 * block on the compiler, defeating KHR_parallel_shader_compile. The
	 * owning program reads it via readCompileStatus() once its link has
	 * completed (and only if the link failed).
	 */
	public compile(source: string): void {
		const gl = this.context.gl
		this.source = source
		this.errors = []
		gl.shaderSource(this.shader, source)
		gl.compileShader(this.shader)
	}

	/**
	 * Populate `errors` from the compile result. COMPILE_STATUS is the
	 * verdict; the info log may hold warnings for a shader that compiled,
	 * and is only parsed when it did not
	 */
	public readCompileStatus(): void {
		const gl = this.context.gl
		if(gl.getShaderParameter(this.shader, WebGL2RenderingContext.COMPILE_STATUS)) {
			this.errors = []
		} else {
			const log = gl.getShaderInfoLog(this.shader)?.trim() ?? ""
			this.errors = ShaderSourceError.parse(this.source, log)
			if(this.errors.length === 0) {
				this.errors = [new ShaderSourceError(log || `${this.type} shader failed to compile`, 0, 0, 0)]
			}
		}
	}
}
